import countries from "i18n-iso-countries";
import { useContext, useEffect, useState } from "react";
import UsersApi from "../../api/usersApi";
import MagnifierIcon from "../../assets/Specialists/magnifier.svg";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../components/MultipleSelect/MultipleSelect";
import { ContextProvider } from "../../contextProvider";
import { TypeSpecialistFilter } from "../../types/TypeSpecialist";
import { TypeUserData } from "../../types/TypeUsers";
import Profile from "../Profile/Profile";
import UserInfo from "./UserInfo/UserInfo";
import s from "./Users.module.css";
import UsersTable from "./UsersTable/UsersTable";

countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const Users = () => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);
  const [isLoader, setIsLoader] = useState(false);
  const [usersData, setUsersData] = useState<
    | (TypeUserData & {
        balanceStatistic?: {
          balance: number;
          income: number;
          reserved: number;
          expenses: number;
          withdrawalInProgress: number;
          withdrawn: number;
        };
      })[]
    | undefined
  >();
  const [page, setPage] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedUser, setSelectedUser] = useState<TypeUserData | undefined>();
  const [avatars, setAvatars] = useState<{ [userId: string]: string }>({});
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isUpdatedProfileModalOpen, setIsUpdatedProfileModalOpen] =
    useState(false);
  const [filters, setFilters] = useState<TypeSpecialistFilter>({});
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [filterUpdateCounter, setFilterUpdateCounter] = useState(0);
  useEffect(() => {
    const makeAsync = async () => {
      if (token && usersData && usersData.length > 0) {
        /* socket.off(chat.newDialogMessage, handleMessage);
        socket.on(chat.newDialogMessage, handleMessage); */

        const promises = usersData.map((spec) =>
          UsersApi.getUserPublicAvatarAndName(spec._id),
        );
        const responses = await Promise.all(promises);
        const avatarsData = responses.reduce(
          (acc, response) => {
            const avatar = response.avatarS3 || response.avatar;
            if (avatar) {
              acc[response._id] = avatar;
            }
            return acc;
          },
          {} as { [userId: string]: string },
        );

        setAvatars(avatarsData);
      }
    };
    makeAsync();
  }, [usersData]);

  useEffect(() => {
    const makeAsync = async () => {
      if (token && userData) {
        const payload = {
          userId: userData._id,
          filters: {
            ...filters,
            name: search,
            email: search,
            page: page + 1,
            limit: rowsPerPage,
            sort: {
              field: "_id",
              type: -1 as -1,
            },
          },
        };
        setIsLoader(true);
        const response = await UsersApi.getFilteredClients(token, payload);

        if (response.paramsAvailable && response.paramsAvailable.totalAmount) {
          setTotalAmount(response.paramsAvailable.totalAmount);
        }
        if (response.users && !response.users.length) {
          setUsersData(undefined);
        }
        if (response.status && response.paramsAvailable) {
          setUsersData(response.users);
        }
        setIsLoader(false);
        if (response.users && !response.users.length) return;
      }
    };
    makeAsync();
  }, [page, rowsPerPage, filterUpdateCounter]);

  const selectUserHandler = (specialistId: string) => {
    if (!usersData) return;
    const selectedUser = usersData.filter(
      (item) => item._id === specialistId,
    )[0];
    setSelectedUser(selectedUser);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilterUpdateCounter((prev) => prev + 1);
      setPage(0);
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearch]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setDebouncedSearch(value);
  };

  const banStatusFilterData = [
    {
      lable: "Banned",
      value: "banned",
    },
    {
      lable: "Not Banned",
      value: "notBanned",
    },
    {
      lable: "All",
      value: "all",
    },
  ];

  return (
    <div className={s.container}>
      <div className={s.headBlock}>
        <div className={s.pageInfoTitleBlock}>
          <h1>Clients</h1>
          {/*       <span>84% verified user profiles</span> */}
        </div>
      </div>
      <div className={s.filterBlock}>
        <div className={s.searchInputBlock}>
          <input
            type="text"
            className={s.searchInput}
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <img src={MagnifierIcon} alt="" className={s.magnifierIcon} />
        </div>
        <MultipleSelect
          data={banStatusFilterData}
          multiplie={false}
          value={
            filters.isBanned
              ? "banned"
              : filters.isBanned === false
                ? "notBanned"
                : "all"
          }
          setValue={(value) => {
            setFilterUpdateCounter((prev) => prev + 1);
            setPage(0);
            setFilters((prev) => ({
              ...prev,
              isBanned:
                value === "banned"
                  ? true
                  : value === "notBanned"
                    ? false
                    : undefined,
            }));
          }}
          width="20%"
          lable="Ban status"
        />
      </div>
      <div className={s.contentBlock}>
        <div className={s.tableBlock}>
          {usersData && usersData.length ? (
            <UsersTable
              rows={usersData}
              page={page}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
              selectSpecialistHandler={selectUserHandler}
              count={totalAmount}
            />
          ) : isLoader ? (
            <div className={s.noSpecialistBlock}>
              <Loader size={80} />{" "}
            </div>
          ) : (
            <div className={s.noSpecialistBlock}>
              <h1>No users found</h1>
            </div>
          )}
        </div>
        {usersData && usersData.length ? (
          <div
            className={`${s.specialistInfoBlock} ${
              selectedUser && s.specialistInfoBlockActive
            }`}
          >
            {selectedUser && (
              <UserInfo
                avatars={avatars}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                setAllUsersData={setUsersData}
              />
            )}
          </div>
        ) : (
          ""
        )}
      </div>
      <ModalWindow
        isOpen={isProfileModalOpen}
        setIsOpen={setIsProfileModalOpen}
        height="90vh"
        width="90vw"
        padding="0 20px"
        /*  bgColor="rgb(46,46,46)" */
      >
        <div className={s.profileContainer}>
          {selectedUser && (
            <Profile
              userId={selectedUser._id}
              isOpen={isProfileModalOpen}
              setIsOpen={setIsProfileModalOpen}
              getPublicInfoString={`${selectedUser.latinName}-${
                selectedUser.latinSurname
              }-${selectedUser._id.slice(-7)}`}
              isPublicProfile
            />
          )}
        </div>
      </ModalWindow>
      <ModalWindow
        isOpen={isUpdatedProfileModalOpen}
        setIsOpen={setIsUpdatedProfileModalOpen}
        height="90vh"
        width="90vw"
        padding="0 20px"
        /*  bgColor="rgb(46,46,46)" */
      >
        <div className={s.profileContainer}>
          {selectedUser && (
            <Profile
              userId={selectedUser._id}
              isOpen={isUpdatedProfileModalOpen}
              setIsOpen={setIsUpdatedProfileModalOpen}
              getPublicInfoString={`${selectedUser.latinName}-${
                selectedUser.latinSurname
              }-${selectedUser._id.slice(-7)}`}
              isPublicProfile={false}
              initialSpecialization={selectedUser._id}
            />
          )}
        </div>
      </ModalWindow>
    </div>
  );
};

export default Users;
